/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { Suspense, useEffect, useState } from "react";
import type { AnimationNames } from "./lottie-animation.helper";
import type { AnimationData, RendererSettings } from "./lottie-animation.types";
import { importLottie } from "./importLottie.func";
import { useCosmosComponentContext } from "@components/context/ComponentContext";

type LottieAnimationProps = { lottie?: AnimationNames; className?: string; rendererSettings?: RendererSettings };

export const LottieAnimation = ({
	lottie,
	className,
	rendererSettings,
}: LottieAnimationProps): React.JSX.Element | null => {
	const { Lottie } = useCosmosComponentContext();
	const [animationData, setAnimationData] = useState<AnimationData | null>(null);

	useEffect(() => {
		setAnimationData(null);
		if (lottie?.length) {
			importLottie(lottie)
				.then((animationData) => setAnimationData(animationData))
				.catch((e) => {
					throw e;
				});
		}
	}, [lottie, setAnimationData]);

	if (!lottie || !Lottie) return null;

	// Every site supplies `Lottie` as a bare `dynamic(() => import("lottie-react"))`
	// — no `loading`, no `ssr: false` — which Next wraps in a Fragment rather than a
	// boundary, so it suspends on first render with nothing to catch it. It used to
	// land in the Suspense that EDSProvider incidentally put around the whole page;
	// that boundary is gone (see TemplateProviders / MW-13210), so own one here. A
	// background animation is exactly what should degrade to nothing while loading.
	return (
		<Suspense fallback={null}>
			<Lottie animationData={animationData} rendererSettings={rendererSettings} className={className} />
		</Suspense>
	);
};
